// Hosting helpers (7.6F.1): how a host builds the shared game session for one
// format variant from a Creative Document — the Studio preview today. They
// resolve the variant with the compiler's `resolveVariant`, so they live apart
// from `game.ts` / `runner.ts`: a delivery runtime imports only the small
// gameplay core and gives it the frames of the compiled artifact's layout
// (the same resolution), never the compiler.

import { resolveVariant } from "./compile.js";
import { GameSession, runnerEnergyFloor, type Rect } from "./game.js";
import type { RunnerGeometry } from "./runner.js";
import type { CreativeDocument } from "./schema/document.js";

/**
 * Geometry of one format variant as the runner needs it: the canvas and the
 * explicit frames resolved by the Creative Engine (never re-derived by a host).
 */
export function runnerGeometry(doc: CreativeDocument, layoutId: string): RunnerGeometry | undefined {
  const layout = doc.layouts.find((l) => l.id === layoutId);
  const resolved = resolveVariant(doc, layoutId);
  if (!layout || !resolved) return undefined;
  const canvas =
    layout.sizing.mode === "fixed"
      ? { width: layout.sizing.width, height: layout.sizing.height }
      : { width: layout.sizing.design.width, height: layout.sizing.design.height };
  return {
    canvas,
    layoutId,
    frame: (id) => {
      const f = (resolved.elements[id] as { frame?: Rect } | undefined)?.frame;
      return f ? { x: f.x, y: f.y, width: f.width, height: f.height } : undefined;
    },
  };
}

/**
 * The game session of `doc` in one format variant, wired exactly as every host
 * must wire it: objectives counted only when present in the variant, the
 * runner on that variant's explicit geometry and the declared energy floor.
 */
export function createGameSession(doc: CreativeDocument, layoutId: string): GameSession | null {
  if (!doc.game) return null;
  const elements = resolveVariant(doc, layoutId)?.elements ?? {};
  const geometry = runnerGeometry(doc, layoutId);
  return new GameSession(
    doc.game,
    (id) => Object.hasOwn(elements, id),
    geometry ? { geometry, energyFloor: runnerEnergyFloor(doc) } : undefined
  );
}
